import { ArrowUp } from "@phosphor-icons/react";
import { Button, Flex, Text, TextArea } from "@radix-ui/themes";
import { useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";

import { askPunttChatRoute } from "./chatRoute";
import { InputPlusMenu } from "./components/InputPlusMenu";
import { WorkflowCards } from "./components/WorkflowCards";
import { useAttachedFiles } from "./hooks/useAttachedFiles";

import { errorAnalyticsPayload, useAnalytics } from "../../utils/analytics";
import { useCreateAskPunttChat } from "../../utils/queries/askPuntt";

const CONNECTORS = [
  { id: "sharepoint", defaultEnabled: true },
  { id: "wrike", defaultEnabled: true },
  { id: "teams", defaultEnabled: false },
] as const;

export default function AskPunttHome() {
  const [inputMessage, setInputMessage] = useState("");
  const navigate = useNavigate();
  const createChat = useCreateAskPunttChat();
  const analytics = useAnalytics("ask_puntt_home");
  const {
    attachedItems,
    addFiles,
    addFile,
    removeFile,
    clearAll,
    readyAttachments,
    hasPending,
  } = useAttachedFiles({
    onUploadError: (err) =>
      analytics.capture("ask_puntt_upload_error", {
        ...errorAnalyticsPayload(err),
      }),
  });
  const [enabledConnectors, setEnabledConnectors] = useState<
    Record<string, boolean>
  >(() => {
    const initial: Record<string, boolean> = {};
    CONNECTORS.forEach((c) => {
      initial[c.id] = c.defaultEnabled;
    });
    return initial;
  });

  useEffect(() => {
    analytics.capture("ask_puntt_home_viewed");
  }, []);

  const handleSubmit = async (message?: string, workflowType?: string) => {
    const msg = message || inputMessage;
    if (!msg.trim()) return;
    if (hasPending) return;

    const wfType = workflowType || "general";
    const attachments = [...readyAttachments];
    const enabledConnectorIds = Object.entries(enabledConnectors)
      .filter(([_, enabled]) => enabled)
      .map(([id]) => id);

    try {
      const result = await createChat.mutateAsync({
        message: msg,
        workflowType: wfType,
        attachments: attachments.length > 0 ? attachments : undefined,
        enabledConnectors: enabledConnectorIds,
      });
      const attachmentIds = attachments.map((att) => att.key);

      analytics.capture("ask_puntt_chat_created", {
        workflow_type: wfType,
        message_length: msg.length,
        has_attachments: attachments.length > 0,
        attachment_count: attachments.length,
        attachment_ids: attachmentIds,
        enabled_connectors: enabledConnectorIds,
      });
      clearAll();
      navigate({
        to: askPunttChatRoute.to,
        params: { chatId: result.chat._id },
      });
    } catch (error) {
      const attachmentIds = attachments.map((att) => att.key);

      analytics.capture("ask_puntt_chat_create_error", {
        workflow_type: wfType,
        has_attachments: attachments.length > 0,
        attachment_count: attachments.length,
        attachment_ids: attachmentIds,
        enabled_connectors: enabledConnectorIds,
        ...errorAnalyticsPayload(error),
      });
      console.error("Failed to create chat:", error);
    }
  };

  const handleWorkflowClick = (workflowType: string) => {
    const workflowLabels: Record<string, string> = {
      compliance_review: "I need a compliance review",
      artwork_review: "I need an artwork review",
      brief_creation: "Help me create a brief",
      claim_generation: "Generate claim options for me",
    };
    analytics.capture("ask_puntt_workflow_card_clicked", {
      workflow_type: workflowType,
      card_title: workflowLabels[workflowType] ?? workflowType,
    });
    handleSubmit(workflowLabels[workflowType] || "Hello", workflowType);
  };

  return (
    <div
      className="ask-puntt-mesh-bg ask-puntt-noise relative flex h-full flex-col"
      data-testid="ask-puntt-home"
    >
      {/* Centered heading + input */}
      <div className="mx-auto flex w-full max-w-[720px] flex-1 flex-col justify-center px-4">
        <div className="stagger-children mb-8 flex flex-col items-center gap-3">
          <div className="animate-glow-ring relative z-10 flex size-16 items-center justify-center rounded-2xl bg-puntt-accent-3 shadow-lg">
            <svg
              width="36"
              height="35"
              viewBox="0 0 30 28"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M22.4939 5.67486H15.8328V2.34435C15.8328 2.12352 15.7451 1.91174 15.589 1.75559C15.4328 1.59944 15.221 1.51172 15.0002 1.51172C14.7794 1.51172 14.5676 1.59944 14.4114 1.75559C14.2553 1.91174 14.1676 2.12352 14.1676 2.34435V5.67486H7.50654C6.62323 5.67486 5.7761 6.02576 5.15151 6.65035C4.52692 7.27494 4.17603 8.12207 4.17603 9.00538V20.6622C4.17603 21.5455 4.52692 22.3926 5.15151 23.0172C5.7761 23.6418 6.62323 23.9927 7.50654 23.9927H22.4939C23.3772 23.9927 24.2243 23.6418 24.8489 23.0172C25.4735 22.3926 25.8244 21.5455 25.8244 20.6622V9.00538C25.8244 8.12207 25.4735 7.27494 24.8489 6.65035C24.2243 6.02576 23.3772 5.67486 22.4939 5.67486Z"
                fill="rgb(var(--puntt-blue-9))"
              />
              <circle
                cx="10.428"
                cy="13.5947"
                r="2.91667"
                stroke="#fff"
                strokeWidth="1.66667"
              />
              <circle
                cx="19.5947"
                cy="13.5947"
                r="2.91667"
                stroke="#fff"
                strokeWidth="1.66667"
              />
            </svg>
          </div>
          <Text
            size="8"
            weight="bold"
            className="text-center text-puntt-neutral-gray-12"
          >
            Ask{" "}
            <span
              style={{
                backgroundImage:
                  "linear-gradient(135deg, rgb(var(--puntt-blue-9)), rgb(var(--puntt-blue-11)))",
                WebkitBackgroundClip: "text",
                color: "transparent",
              }}
            >
              Puntt
            </span>
          </Text>
          <Text
            size="4"
            className="text-center tracking-wide text-puntt-neutral-gray-11"
          >
            Your AI compliance assistant
          </Text>
        </div>

        <div className="relative z-10 rounded-2xl border border-puntt-neutral-gray-5/80 bg-base-white shadow-lg transition-all duration-200 focus-within:border-puntt-accent-7 focus-within:shadow-xl focus-within:ring-2 focus-within:ring-puntt-accent-6/30">
          <TextArea
            data-testid="ask-puntt-home-input"
            size="3"
            variant="surface"
            placeholder="Ask Puntt anything..."
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleSubmit();
              }
            }}
            className="w-full resize-none border-none !shadow-none"
            style={{ height: "88px", outline: "none", boxShadow: "none" }}
          />

          {/* Attached files preview */}
          {attachedItems.length > 0 && (
            <Flex gap="2" wrap="wrap" className="px-3 pb-1">
              {attachedItems.map((item, idx) => (
                <Flex
                  key={item.id}
                  align="center"
                  gap="1"
                  className="rounded-md bg-puntt-neutral-gray-3 px-2 py-1"
                >
                  <Text
                    size="1"
                    className="max-w-[150px] truncate text-puntt-neutral-gray-11"
                  >
                    {item.file.name}
                    {item.status === "pending" && " (uploading…)"}
                    {item.status === "error" && " (failed)"}
                  </Text>
                  <button
                    onClick={() => removeFile(idx)}
                    className="text-puntt-neutral-gray-9 hover:text-puntt-neutral-gray-12"
                  >
                    ×
                  </button>
                </Flex>
              ))}
            </Flex>
          )}

          <Flex justify="between" align="center" className="px-3 py-2">
            <InputPlusMenu
              iconSize={16}
              onFilesSelected={addFiles}
              onScreenshotCaptured={addFile}
              enabledConnectors={enabledConnectors}
              onConnectorToggle={(id, enabled) => {
                setEnabledConnectors((prev) => ({ ...prev, [id]: enabled }));
              }}
            />

            <Button
              data-testid="ask-puntt-home-submit"
              size="3"
              className="cursor-pointer rounded-xl"
              disabled={
                !inputMessage.trim() || createChat.isPending || hasPending
              }
              onClick={() => handleSubmit()}
            >
              <ArrowUp size={14} weight="bold" />
              Ask Puntt
            </Button>
          </Flex>
        </div>
      </div>

      {/* Workflows anchored to bottom */}
      <div className="relative z-10 mx-auto w-full max-w-[948px] shrink-0 px-4 pb-6">
        <Flex justify="between" align="center" className="mb-3">
          <Text size="4" weight="bold" className="text-puntt-neutral-gray-12">
            Workflows
          </Text>
          <Button variant="ghost" color="gray" size="1">
            See All →
          </Button>
        </Flex>
        <WorkflowCards onCardClick={handleWorkflowClick} />
      </div>
    </div>
  );
}
